import React from 'react'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Alert from '@mui/material/Alert'
import Typography from '@mui/material/Typography'
import CircularProgress from '@mui/material/CircularProgress'
import update from 'immutability-helper'
import { useNavigate } from 'react-router'
import useDebounce from 'hooks/useDebounce'
import { useAPI } from 'contexts/APIProvider'
import { useAuthUser } from 'contexts/AuthUserProvider'
import FormField from 'components/FormField'
import ErrorList from 'components/ErrorList'
import PricingInfoButton from 'components/PricingInfoButton'
import Header from 'components/Header'
import LeadAvailabilityCheckFields from 'pages/LeadDetail/components/LeadAvailabilityCheckFields'
import { LeadForm, LeadAvailabilityCheckForm, EstimateCost, NursePaygradeStats } from 'types/interfaces'
import LeadFormError from 'errors/LeadFormError'
import LeadAvailabilityCheckFormError from 'errors/LeadAvailabilityCheckFormError'
import EstimateCostError from 'errors/EstimateCostError'
import { formatCurrencyAmount } from 'logic/helpers'
import { DateTime } from 'luxon'
import { round } from 'lodash'
import { useGTMDispatch } from '@elgorditosalsero/react-gtm-hook'
import { Link } from '@mui/material'
import { loginURL, resetPasswordURL } from 'routes/urls'

const LeadCreate: React.FC = () => {
  const { api } = useAPI()
  const { authUser, signInAsGuest, refreshUser } = useAuthUser()
  const navigate = useNavigate()
  const sendDataToGTM = useGTMDispatch()

  const [leadForm, setLeadForm] = React.useState<LeadForm>({})
  const [leadError, setLeadError] = React.useState<LeadFormError>()
  const [checkForm, setCheckForm] = React.useState<LeadAvailabilityCheckForm>({
    date: DateTime.now().plus({ days: 1 }).toFormat('yyyy-MM-dd'),
    start_time: '09:00',
    end_time: '17:00',
    lunch_break: 30,
    role: 'dental nurse',
  })
  const [checkError, setCheckError] = React.useState<LeadAvailabilityCheckFormError>()
  const [loading, setLoading] = React.useState(false)
  const [paygradeStats, setPaygradeStats] = React.useState<NursePaygradeStats>()
  const [estimatedCost, setEstimatedCost] = React.useState<EstimateCost>()

  const debouncedCheckForm = useDebounce(checkForm, 1000)

  const fetchPaygradeStats = React.useCallback(async () => {
    try {
      setPaygradeStats(await api.getNursePaygradeStats())
    } catch (e) {
      console.error('Failed to load paygrade stats', e)
    }
  }, [api])

  React.useEffect(() => {
    fetchPaygradeStats()
  }, [fetchPaygradeStats])

  const fetchEstimatedCost = React.useCallback(async () => {
    if (!paygradeStats || !debouncedCheckForm.date || !debouncedCheckForm.start_time || !debouncedCheckForm.end_time) return
    try {
      setEstimatedCost(await api.getEstimatedCost({
        date: debouncedCheckForm.date,
        start_time: debouncedCheckForm.start_time,
        end_time: debouncedCheckForm.end_time,
        lunch_break: debouncedCheckForm.lunch_break,
        headcount: 1,
        role: debouncedCheckForm.role,
        specialist_required: false,
        min_rate: paygradeStats.min_rate,
        max_rate: paygradeStats.max_rate,
        median_rate: paygradeStats.median_rate,
      }))
    } catch (e) {
      if (e instanceof EstimateCostError) {
        setCheckError(e as any)
      } else {
        console.error('Failed to load estimated cost', e)
      }
    }
  }, [api, paygradeStats, debouncedCheckForm])

  React.useEffect(() => {
    fetchEstimatedCost()
  }, [fetchEstimatedCost])

  const updateLeadForm = React.useCallback((name: string, value: any) => {
    setLeadForm(prev => update(prev, { [name]: { $set: value } }))
    setLeadError(prev => prev ? update(prev, { [name]: { $set: [] } }) : prev)
  }, [])

  const updateCheckForm = React.useCallback((name: string, value: any) => {
    setCheckForm(prev => update(prev, { [name]: { $set: value } }))
    setCheckError(prev => prev ? update(prev, { [name]: { $set: [] } }) : prev)
  }, [])

  const handleSubmit = React.useCallback(async () => {
    setLoading(true)
    setLeadError(undefined)
    setCheckError(undefined)

    const cleanedLeadForm = {
      ...leadForm,
      email: leadForm.email?.trim(),
      phone: leadForm.phone?.trim(),
      practice_name: leadForm.practice_name?.trim(),
      parent_company: leadForm.parent_company?.trim(),
      first_name: leadForm.first_name?.trim(),
      last_name: leadForm.last_name?.trim(),
    }

    try {
      // Dry-run validation: validate lead form and availability check inputs
      await api.createLead(cleanedLeadForm, true)
      await api.createLeadAvailabilityCheck({ ...checkForm, lead_id: 0 }, true)

      // Validation passed — perform mutations
      if (!authUser) {
        await signInAsGuest(cleanedLeadForm.email ?? '')
      }

      const lead = await api.createLead(cleanedLeadForm)
      await api.createLeadAvailabilityCheck({
        ...checkForm,
        lead_id: lead.id,
      })
      sendDataToGTM({ event: 'lead-created' })
      await refreshUser(true)
      navigate(`/lead/${lead.id}/`)
    } catch (e: any) {
      if (e instanceof LeadFormError) {
        setLeadError(e)
      } else if (e instanceof LeadAvailabilityCheckFormError) {
        setCheckError(e)
      } else {
        console.error('unhandled exception', e)
      }
    } finally {
      setLoading(false)
    }
  }, [api, leadForm, checkForm, authUser, sendDataToGTM, refreshUser, navigate, signInAsGuest])

  const isExistingEmailError = React.useMemo(() => {
    return leadError?.schema?.some((error) => error.includes('email is already associated with an existing account'))
  }, [leadError])

  return (
    <Box sx={{ maxWidth: 600, mx: 'auto', mt: 4, p: 2 }}>
      <Header text="Book a Dental Nurse" />
      <Typography variant="body1" color="text.secondary" sx={{ mt: 2, mb: 2 }}>
        Tell us when you need a nurse and we'll find the best available match in your area. <br/>
        <strong>This check is completely free — no commitment required.</strong>
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <FormField
          label="Practice Name"
          value={leadForm.practice_name ?? ''}
          onChange={(e) => updateLeadForm('practice_name', e.target.value)}
          errors={leadError?.practice_name}
          helperText="The name of your dental practice"
        />
        <FormField
          label="Corporate Group Name"
          value={leadForm.parent_company ?? ''}
          onChange={(e) => updateLeadForm('parent_company', e.target.value)}
          errors={leadError?.parent_company}
          helperText="If part of a group, enter the group name (optional)"
        />
        <Typography variant="h6" sx={{ mt: 2 }}>When do you need a nurse?</Typography>
        <LeadAvailabilityCheckFields
          form={checkForm}
          error={checkError}
          updateForm={updateCheckForm}
        />
        {estimatedCost && (
          <Alert severity="info">
            <Typography>
              Estimated total price: <strong>{formatCurrencyAmount(estimatedCost.median_total, 'GBP')} per day</strong> ({round(estimatedCost.billable_hours, 1)} hours total)
            </Typography>
            {estimatedCost.discount ? (
              <Typography>{estimatedCost.discount}</Typography>
            ) : null}
            <Typography variant="caption">This is an estimate. Final charges may vary.</Typography>
          </Alert>
        )}
        <Typography variant="h6" sx={{ mt: 2 }}>Who shall we contact when a nurse is found?</Typography>
        <FormField
          label="First Name"
          value={leadForm.first_name ?? ''}
          onChange={(e) => updateLeadForm('first_name', e.target.value)}
          errors={leadError?.first_name}
        />
        <FormField
          label="Last Name"
          value={leadForm.last_name ?? ''}
          onChange={(e) => updateLeadForm('last_name', e.target.value)}
          errors={leadError?.last_name}
        />
        <FormField
          label="Email"
          value={leadForm.email ?? ''}
          onChange={(e) => updateLeadForm('email', e.target.value)}
          errors={leadError?.email}
        />
        <FormField
          label="Phone"
          value={leadForm.phone ?? ''}
          onChange={(e) => updateLeadForm('phone', e.target.value)}
          errors={leadError?.phone}
          helperText="e.g. 07700 900000 or +44 7700 900000"
        />
        {leadError?.schema?.length ? (
          <Alert severity="error" sx={{ mb: 2 }} variant='filled'>
            <ErrorList errors={leadError.schema} />
          </Alert>
        ) : null}
        {isExistingEmailError ? (
          <Alert severity="info" sx={{ mb: 2 }} variant='outlined'>
            Forgot your password? <Link href={resetPasswordURL()}>Click here to reset it</Link> or <Link href={loginURL()}>login here</Link>
          </Alert>
        ) : null}
        {checkError?.schema?.length ? (
          <Alert severity="error" sx={{ mb: 2 }} variant='filled'>
            <ErrorList errors={checkError.schema} />
          </Alert>
        ) : null}
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={loading}
        >
          {loading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={20} color="inherit" />
              Checking availability...
            </Box>
          ) : (
            'Check Availability — It\'s Free'
          )}
        </Button>
        <PricingInfoButton paygradeStats={paygradeStats} />
      </Box>
    </Box>
  )
}

export default LeadCreate
